import { useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Brand } from '../ui/components/Brand.tsx';
import { SegmentedNav, type NavigationItem } from '../ui/components/Navigation.tsx';
import { AmbientField } from '../ui/primitives/AmbientField.tsx';
import { Avatar } from '../ui/primitives/Avatar.tsx';
import { useCurrentUser } from './AuthProvider.tsx';
import { DevRoleSwitcher } from '../features/dev/DevAuth.tsx';
import styles from './AppLayout.module.css';

/**
 * The persistent shell for the BROWSING screens — the journey, a Lesson, the
 * account — and deliberately not for practice.
 *
 * Design system § 14 gives navigation two patterns and no third: the
 * segmented pill for moving between places, and the task bar for a focused
 * task that has one escape. Practice is a focused task, so it sits outside
 * this layout and keeps its own exit; wrapping it here would put a "go
 * somewhere else" affordance beside an exercise, which is the opposite of
 * what § 1 asks a practice screen to feel like.
 *
 * There is no sidebar at any viewport (§ 2 lists it first among the
 * anti-patterns), so the pill is the whole of it: inline on a wide screen,
 * docked to the bottom on a narrow one (§ 11), which `SegmentedNav` already
 * knows how to do.
 *
 * The shell is also where the two things every browsing screen shares live:
 * the wordmark, so the product says its name once per page rather than once
 * per screen that remembers to; and § 8's L0, which is defined as
 * `background` PLUS `grad/ambient` and is therefore not the business of any
 * single screen. Practice, sitting outside this layout, carries its own
 * ground for the same reason.
 */

/**
 * Which tab is lit for a given path.
 *
 * `/` is the only address the spine has — a Lesson opens ON it, not at an
 * address of its own — so the tests below name the other places and
 * everything else falls through to "Aprenda". `/revisao` is what that
 * fallback is carrying: it looks back into the spine's own material, and the
 * pill must never go dark because the learner stepped into a place Aprenda
 * leads to.
 */
function currentTab(pathname: string): string {
  if (pathname.startsWith('/ranking')) return '/ranking';
  /*
   * SOMEBODY ELSE'S profile is where the ranking leads, so the pill stays on
   * Ranking rather than going dark on a place Ranking sent you to — the same
   * reason `/revisao` falls through to Aprenda below.
   *
   * YOUR OWN is not, and must not light Ranking: the avatar in this band is
   * what leads there, and a pill claiming "you are in Ranking" while the
   * thing the reader actually pressed sits unmarked at the other end of the
   * band is a lie about where they are. `/perfil` and `/conta` are the two
   * addresses the account object owns, so it carries the mark for both
   * (`isAccountPlace`) and the pill carries none.
   */
  if (pathname === '/perfil' || pathname.startsWith('/conta')) return '';
  if (pathname.startsWith('/perfil')) return '/ranking';
  if (pathname.startsWith('/admin')) return '/admin';
  return '/';
}

/** The two places the avatar in the band leads to, and therefore marks. */
function isAccountPlace(pathname: string): boolean {
  return pathname === '/perfil' || pathname.startsWith('/conta');
}

/**
 * The administrative areas, in the order their tab sits in the pill. They are
 * named from day one — see `AdminAreaPlaceholder` for the ones still to come
 * — because the shape of the admin area should be visible before every block
 * inside it is built out.
 *
 * THE RANKING IS NOT ONE OF THEM. It is a learner-facing screen, and an
 * administrative copy of it would answer a question nobody running this
 * course has asked: who is ahead this week is something the learners compete
 * over, not something the operator administers. `/ranking` is the whole of
 * it.
 */
const ADMIN_AREAS: { id: string; label: string }[] = [
  { id: '', label: 'Visão geral' },
  { id: 'conteudo', label: 'Conteúdo' },
  { id: 'usuarios', label: 'Usuários' },
  { id: 'atividade', label: 'Atividade' },
  { id: 'pagamentos', label: 'Pagamentos' },
  { id: 'comunidade', label: 'Comunidade' },
  { id: 'eventos', label: 'Eventos' },
];

export function AppLayout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const user = useCurrentUser();
  const tab = currentTab(pathname);
  const inAdmin = tab === '/admin';

  /*
   * "Back" in the admin nav means back to wherever the administrator actually
   * came from, not unconditionally to "/" — so the last non-admin path is
   * remembered as it changes, and the ref (not state) is what the back
   * control reads, since it must never itself trigger a render.
   */
  const lastNonAdminPath = useRef('/');
  useEffect(() => {
    if (!pathname.startsWith('/admin')) {
      lastNonAdminPath.current = pathname;
    }
  }, [pathname]);

  const adminArea = pathname.split('/')[2] ?? '';
  const adminItems: NavigationItem[] = ADMIN_AREAS.map((area) => ({
    id: area.id,
    label: area.label,
    current: area.id === adminArea,
  }));

  // The administrative tab is not merely hidden from a learner: the server is
  // the authority (every /api/admin/* route is 403 for them), and the route
  // itself is gated. This is the third layer, and the only one whose job is
  // to keep the pill honest about where this person can actually go.
  const items: NavigationItem[] = [
    { id: '/', label: 'Aprenda', current: tab === '/' },
    { id: '/ranking', label: 'Ranking', current: tab === '/ranking' },
    ...(user?.role === 'admin'
      ? [{ id: '/admin', label: 'Administração', current: tab === '/admin' }]
      : []),
  ];

  return (
    <div className={styles.shell}>
      {/* § 8 L0 — the page's own atmosphere, behind every browsing screen and
          clipped to the shell so no blob can widen the document (SC-015). */}
      <AmbientField count={3} anchor="viewport" />

      {/* Renders nothing outside `pnpm dev` (features/dev/DevAuth.tsx). */}
      <DevRoleSwitcher />

      {/*
        Brand, navigation and the person are ONE BAND, in that order: the
        product's name at the start, where it can be read first; the pill
        centred, because it is the band's subject and the two things flanking
        it are labels rather than choices; the account at the end, which is
        where a reader looks for themselves.

        Below 768 the pill leaves the band for the bottom of the viewport
        (§ 11 — thumb reach) and the band closes to the two identity objects,
        which is the same composition at another measure rather than a second
        one.

        Everything here is a LABEL on the page, not a heading: the screen's own
        `h1` belongs to the screen. The account is a link into `/perfil` rather
        than a menu — § 14 gives navigation two patterns and a dropdown is not
        one of them.

        IT LEADS TO THE PERSON, NOT TO THE SETTINGS. Pressing your own face is
        a request to see yourself, and what answers it is the profile: the
        streak, the total, the weeks. `/conta` is where note names and the
        delete control live, which is somewhere a learner goes once — reached
        from the profile rather than standing in front of it.
      */}
      <header className={styles.band}>
        <div className={styles.brandSlot}>
          <Brand mark />
        </div>

        <div className={styles.nav}>
          <SegmentedNav
            items={items}
            label="Navegação principal"
            placement="floating"
            /*
              TWO KINDS OF ID MEET IN ONE PILL, and the leading slash is what
              tells them apart. A place in the product carries its own address
              ("/", "/ranking", "/admin"); an administrative area carries only
              its segment ("conteudo", "" for the overview), because it is
              always read under /admin. Testing for the slash rather than
              listing the top-level routes is what keeps a new tab from
              silently becoming an admin address — which is exactly what
              happened to Ranking.
            */
            onSelect={(id) =>
              navigate(id.startsWith('/') ? id : id ? `/admin/${id}` : '/admin')
            }
            expand={
              user?.role === 'admin'
                ? {
                    triggerId: '/admin',
                    open: inAdmin,
                    items: adminItems,
                    backLabel: 'Voltar',
                    onBack: () => navigate(lastNonAdminPath.current),
                  }
                : undefined
            }
          />
        </div>

        {user ? (
          <button
            type="button"
            className={styles.account}
            onClick={() => navigate('/perfil')}
            aria-current={isAccountPlace(pathname) ? 'page' : undefined}
            aria-label={`Seu perfil — ${user.name}`}
          >
            <Avatar name={user.name} image={user.image} />
          </button>
        ) : (
          /* Keeps the pill centred while the account resolves, so the band
             does not shift under the reader on first paint. */
          <span className={styles.account} aria-hidden="true" />
        )}
      </header>

      <main className={styles.main}>
        <Outlet />
      </main>
    </div>
  );
}
